import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Download, Printer } from 'lucide-react';
import PromotionKpiCards from './promotion-kpi-cards';
import PromotionBreakdownTable from './promotion-breakdown-table';
import PromotionTrendChart from './promotion-trend-chart';
import PromotionCouponTable from './promotion-coupon-table';
import PromotionDetailTable from './promotion-detail-table';
import { exportPromotionReportCsv } from './promotion-csv';
import {
  customerName,
  customerPhone,
  formatCurrency,
  formatEnum,
  formatNumber,
} from './promotion-report-format';

const currencyCell = (key) => (row) => formatCurrency(row[key]);
const numberCell = (key) => (row) => formatNumber(row[key]);

export default function PromotionReportOutput({
  report,
  detailItems,
  detailTotal,
  isLoadingMore,
  hasMoreData,
  onLoadMore,
  onPrint,
  isPreparingPrint,
}) {
  const { filters, timezone } = report;

  return (
    <div>
      <div className="no-print flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">
            {filters?.startDate} {filters?.startTime} &ndash; {filters?.endDate}{' '}
            {filters?.endTime}
          </Badge>
          <Badge variant="outline">
            {filters?.useLocalTime ? timezone : 'UTC'}
          </Badge>
          {filters?.status && (
            <Badge variant="outline">{formatEnum(filters.status)}</Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onPrint}
            disabled={isPreparingPrint}
          >
            <Printer className="h-4 w-4 mr-2" />
            {isPreparingPrint ? 'Preparing…' : 'Print'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportPromotionReportCsv({ ...report, detailItems })}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      <div id="promotion-report" className="space-y-8">
        <PromotionKpiCards summary={report.summary} />

        <PromotionTrendChart
          dailyTrend={report.dailyTrend}
          timezone={timezone}
        />

        <PromotionBreakdownTable
          title="By Promotion"
          columns={[
            {
              id: 'promotionName',
              label: 'Promotion',
              render: (row) => (
                <span className="font-medium">{row.promotionName}</span>
              ),
            },
            {
              id: 'type',
              label: 'Type',
              render: (row) => formatEnum(row.type),
            },
            {
              id: 'usageCount',
              label: 'Usages',
              align: 'right',
              render: numberCell('usageCount'),
            },
            {
              id: 'uniqueOrderCount',
              label: 'Orders',
              align: 'right',
              render: numberCell('uniqueOrderCount'),
            },
            {
              id: 'totalDiscount',
              label: 'Discount',
              align: 'right',
              render: currencyCell('totalDiscount'),
            },
            {
              id: 'avgDiscount',
              label: 'Avg Discount',
              align: 'right',
              render: currencyCell('avgDiscount'),
            },
          ]}
          rows={(report.byPromotion || []).map((row) => ({
            ...row,
            rowKey: row.promotionUuid,
          }))}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <PromotionBreakdownTable
            title="By Channel"
            columns={[
              {
                id: 'channel',
                label: 'Channel',
                render: (row) => formatEnum(row.channel),
              },
              {
                id: 'usageCount',
                label: 'Usages',
                align: 'right',
                render: numberCell('usageCount'),
              },
              {
                id: 'uniqueOrderCount',
                label: 'Orders',
                align: 'right',
                render: numberCell('uniqueOrderCount'),
              },
              {
                id: 'totalDiscount',
                label: 'Discount',
                align: 'right',
                render: currencyCell('totalDiscount'),
              },
            ]}
            rows={(report.byChannel || []).map((row) => ({
              ...row,
              rowKey: row.channel,
            }))}
          />

          <PromotionBreakdownTable
            title="By Promotion Type"
            columns={[
              {
                id: 'type',
                label: 'Type',
                render: (row) => formatEnum(row.type),
              },
              {
                id: 'usageCount',
                label: 'Usages',
                align: 'right',
                render: numberCell('usageCount'),
              },
              {
                id: 'totalDiscount',
                label: 'Discount',
                align: 'right',
                render: currencyCell('totalDiscount'),
              },
            ]}
            rows={(report.byType || []).map((row) => ({
              ...row,
              rowKey: row.type,
            }))}
          />

          <PromotionBreakdownTable
            title="By Status"
            description="Counts every status in the period, not just the one filtered on."
            columns={[
              {
                id: 'status',
                label: 'Status',
                render: (row) => (
                  <Badge variant="outline">{formatEnum(row.status)}</Badge>
                ),
              },
              {
                id: 'usageCount',
                label: 'Usages',
                align: 'right',
                render: numberCell('usageCount'),
              },
              {
                id: 'totalDiscount',
                label: 'Discount',
                align: 'right',
                render: currencyCell('totalDiscount'),
              },
            ]}
            rows={(report.byStatus || []).map((row) => ({
              ...row,
              rowKey: row.status,
            }))}
          />

          <PromotionBreakdownTable
            title="Top Customers"
            description="Top 20 by discount received. Anonymous usages are excluded."
            columns={[
              {
                id: 'customerName',
                label: 'Customer',
                render: (row) => customerName(row) || '-',
              },
              {
                id: 'customerPhone',
                label: 'Phone',
                render: (row) => customerPhone(row) || '-',
              },
              {
                id: 'usageCount',
                label: 'Usages',
                align: 'right',
                render: numberCell('usageCount'),
              },
              {
                id: 'totalDiscount',
                label: 'Discount',
                align: 'right',
                render: currencyCell('totalDiscount'),
              },
            ]}
            rows={(report.topCustomers || []).map((row) => ({
              ...row,
              rowKey: row.customerId,
            }))}
          />
        </div>

        <PromotionCouponTable coupons={report.coupons} />

        <PromotionDetailTable
          items={detailItems}
          total={detailTotal}
          isLoadingMore={isLoadingMore}
          hasMoreData={hasMoreData}
          onLoadMore={onLoadMore}
        />
      </div>
    </div>
  );
}
